/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description RentableMarker
 */
import { Box, type Interpolation } from '@chakra-ui/react'
import { Marker } from '@vis.gl/react-mapbox'
import { useHover } from '@zcode-ui/hook'
import React from 'react'

import { useMarkerBg } from '../../../helpers'

import * as styles from './style'

export interface RentableMarkerProps {
  analyticChannel: string
  cluster: { [key: string]: any }
  isHovered: boolean
  isVisited: boolean
  lat: number
  lng: number
  onClick: ({
    analyticChannel,
    cluster,
  }: {
    analyticChannel: string
    cluster: { [key: string]: any }
  }) => void
  onMouseOut: () => void
  onMouseOver: (cluster: { [key: string]: any }) => void
  text: string
}

export const RentableMarker = ({
  analyticChannel,
  cluster,
  isHovered,
  isVisited,
  lat,
  lng,
  onClick,
  onMouseOut,
  onMouseOver,
  text,
}: RentableMarkerProps) => {
  const [markerRef, isMarkerHovered] = useHover<HTMLDivElement>()

  const onMarkerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event) {
      event.stopPropagation()
      event.preventDefault()
    }

    onClick?.({ analyticChannel, cluster })
  }
  const onMarkerMouseOver = () => {
    onMouseOver?.(cluster)
  }

  const markerBg = useMarkerBg({ isVisited })

  // check if price is 0
  if (text === '$0' || text === 'N/A') {
    return null
  }

  return (
    <Marker
      anchor="bottom"
      css={styles.rentableMarker}
      draggable={false}
      latitude={lat}
      longitude={lng}
      style={{ zIndex: isHovered || isMarkerHovered ? 3 : 'unset' }}
    >
      <div css={[styles.rentableMarker, isHovered && styles.hover, isVisited && styles.visited]}>
        <Box<'div'>
          bg={markerBg}
          css={
            [
              styles.rentableMarkerContent,
              isVisited && styles.visitedRentableMarkerContent,
              (isHovered || isMarkerHovered) && styles.hoveredRentableMarkerContent,
            ] as Interpolation<{}>
          }
          onClick={onMarkerClick}
          onMouseOut={onMouseOut}
          onMouseOver={onMarkerMouseOver}
          ref={markerRef}
          role="presentation"
        >
          <div css={styles.text}>{text}</div>
        </Box>
        <div css={styles.rentableMarkerArrow}>
          <Box<'div'>
            bg={markerBg}
            css={
              [
                styles.verticalBar,
                isVisited && styles.visitedVerticalBar,
                (isHovered || isMarkerHovered) && styles.hoveredVerticalBar,
              ] as Interpolation<{}>
            }
          />
          <Box<'div'>
            bg={markerBg}
            css={
              [
                styles.circle,
                isVisited && styles.visitedCircle,
                (isHovered || isMarkerHovered) && styles.hoveredCircle,
              ] as Interpolation<{}>
            }
          />
        </div>
      </div>
    </Marker>
  )
}
